// App
import React from 'react';

// Components
import BubbleWindow from './BubbleWindow';
import HomeDot from './HomeDot';

// Hooks
import { useBoundingRect } from './hooks';

// zillow-map-control
import { Bubble, ZillowMapControl, ZillowMapLayer } from '@zillow/zillow-map-control';

// utils
import { currencyStringToInt, kFormat } from '../utils/numberUtils';

// Types
import { useAppDispatch, useAppSelector } from '../flux/configureStore';
import type ComparablesModelType from '../models/Comparables';
import { setActiveId, setHoverId } from '../flux/actions/UIActions';
import type { AppThunkVoid } from '../flux/types';

export interface MapProps {
  useBubble: boolean;
}

const Map: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<MapProps>>> = ({
  useBubble = true,
}) => {
  // Redux
  const dispatch = useAppDispatch();
  const GMAP_KEY: string = useAppSelector((state) => state.app.googleMapsApiKey);
  const comparables: Array<ComparablesModelType> = useAppSelector((state) => state.comparables.items);
  const userProperty = useAppSelector((state) => state.address);
  const hoverId: string = useAppSelector((state) => state.ui.hoverId);
  const activeId: string = useAppSelector((state) => state.ui.activeId);
  const homeId: string = useAppSelector((state) => state.address.listingId);

  // Custom hooks
  const [boundingRect] = useBoundingRect(userProperty, comparables);

  // Callbacks
  const getCallbacks = (listingId: string | null) => {
    if (!listingId) {
      return {
        handleMouseEnter: () => {},
        handleMouseLeave: () => {},
        handleMouseClick: () => {},
      };
    }

    return {
      handleMouseEnter: (): AppThunkVoid<void> => dispatch(setHoverId(listingId)),
      handleMouseLeave: (): AppThunkVoid<void> => dispatch(setHoverId(null)),
      handleMouseClick: (): AppThunkVoid<void> => dispatch(setActiveId(listingId)),
    };
  };

  const renderMarkers = () => {
    const processedMarkers = [...comparables].reduce((accumulator, currentMarker, idx) => {
      const { isUsersComp, isActive, listingId, lat, lon, bubblePrice } = currentMarker;

      const { handleMouseEnter, handleMouseLeave, handleMouseClick } = getCallbacks(listingId);
      const markerData = {
        statusType: isActive ? 'FOR_RENT' : 'OTHER',
        northSouthLat: lat,
        eastWestLng: lon,
        markerId: listingId?.toString(),
        onMapMarkerMouseEnter: handleMouseEnter,
        onMapMarkerMouseLeave: handleMouseLeave,
        onMarkerClick: handleMouseClick,
        isHovered: hoverId === listingId || activeId == listingId,
        showDot: true,
        key: `map-dot-${listingId}-${idx}`,
        markerLabel: !!bubblePrice && kFormat(currencyStringToInt(bubblePrice)),
      };

      if (!isUsersComp) {
        return accumulator.concat(markerData);
      } else {
        return accumulator;
      }
    }, [] as any);

    return processedMarkers;
  };

  const renderBubble = () => {
    const hoveredProperty = [...comparables].find((comp) => comp.listingId === hoverId && comp.listingId !== homeId);

    if (!hoveredProperty) {
      return null;
    }

    const { lat, lon, propertyPhotos, beds, baths, sqft, bubblePrice } = hoveredProperty;

    return (
      <ZillowMapLayer ariaHidden>
        {useBubble && (
          <Bubble
            northSouthLat={lat}
            eastWestLng={lon}
            markerDimensions={{ x: 15, y: 15 }}
            contentWidth={136}
            contentHeight={64}
          >
            <BubbleWindow
              propertyPhotos={propertyPhotos}
              beds={beds}
              baths={baths}
              sqft={sqft}
              bubblePrice={bubblePrice}
            />
          </Bubble>
        )}
      </ZillowMapLayer>
    );
  };

  const renderUserProperty = () => {
    const { listingId, lat, lon } = userProperty;
    const { handleMouseEnter, handleMouseLeave, handleMouseClick } = getCallbacks(listingId);
    return (
      <ZillowMapLayer ariaHidden>
        <HomeDot
          northSouthLat={lat}
          eastWestLng={lon}
          onMapMarkerMouseEnter={handleMouseEnter}
          onMapMarkerMouseLeave={handleMouseLeave}
          onMarkerClick={handleMouseClick}
        />
      </ZillowMapLayer>
    );
  };

  return (
    <ZillowMapControl apiKey={GMAP_KEY} gestureHandling="cooperative" boundingRect={boundingRect}>
      <ZillowMapLayer markers={renderMarkers()} ariaHidden>
        {renderBubble()}
        {renderUserProperty()}
      </ZillowMapLayer>
    </ZillowMapControl>
  );
};

export default Map;
